import { useEffect, useMemo, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { useTeamStore } from '@/stores/team'
import { getCurrentTeamId } from '@/utils/auth'
import {
  broadcastPlatformContextSwitched,
  broadcastTeamContextSwitched,
  consumeContextSyncNavigationFlag,
} from '@/utils/contextSync'

type RouteContext =
  | { mode: 'platform'; path: string }
  | { mode: 'team'; path: string; teamId?: number }

function isHistoryTraversalNavigation(): boolean {
  if (typeof window === 'undefined') return false

  const navigationEntries = window.performance.getEntriesByType(
    'navigation',
  ) as PerformanceNavigationTiming[]
  if (navigationEntries.length > 0) {
    return navigationEntries[0].type === 'back_forward'
  }

  // Fallback for older browsers.
  return window.performance.navigation?.type === 2
}

function broadcastCurrentContext(context: RouteContext): void {
  if (context.mode === 'platform') {
    broadcastPlatformContextSwitched(context.path)
    return
  }

  if (typeof context.teamId === 'number') {
    broadcastTeamContextSwitched(context.path, context.teamId, false)
  }
}

function ContextSyncOrchestrator() {
  const location = useLocation()
  const teamId = useTeamStore((state) => state.teamTokenData?.teamId)
  const lastContextRef = useRef<RouteContext | null>(null)
  const latestContextRef = useRef<RouteContext | null>(null)

  const currentContext = useMemo<RouteContext>(() => {
    const path = `${location.pathname}${location.search}`
    if (location.pathname.startsWith('/team')) {
      return { mode: 'team', path, teamId: teamId ?? getCurrentTeamId() ?? undefined }
    }
    return { mode: 'platform', path }
  }, [location.pathname, location.search, teamId])

  useEffect(() => {
    latestContextRef.current = currentContext
  }, [currentContext])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted && !isHistoryTraversalNavigation()) return
      const context = latestContextRef.current
      if (!context) return
      broadcastCurrentContext(context)
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  useEffect(() => {
    const shouldSkipBroadcast = consumeContextSyncNavigationFlag()
    const previous = lastContextRef.current
    lastContextRef.current = currentContext

    if (shouldSkipBroadcast) return

    // Browser back/forward can restore this app without previous in-memory context.
    // In that case, proactively publish current context once to keep other tabs aligned.
    if (!previous) {
      if (isHistoryTraversalNavigation()) {
        broadcastCurrentContext(currentContext)
      }
      return
    }

    if (previous.mode !== currentContext.mode) {
      if (currentContext.mode === 'platform') {
        broadcastPlatformContextSwitched(currentContext.path)
      } else if (typeof currentContext.teamId === 'number') {
        broadcastTeamContextSwitched(currentContext.path, currentContext.teamId, false)
      }
      return
    }

    if (
      previous.mode === 'team' &&
      currentContext.mode === 'team' &&
      typeof previous.teamId === 'number' &&
      typeof currentContext.teamId === 'number' &&
      previous.teamId !== currentContext.teamId
    ) {
      broadcastTeamContextSwitched(currentContext.path, currentContext.teamId, true)
    }
  }, [currentContext])

  return null
}

export function Root() {
  return (
    <>
      <ContextSyncOrchestrator />
      <Outlet />
    </>
  )
}
